import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FlexColumn } from '@elementary/common-ui/components';
import useEnvironmentsData from 'shared/hooks/useEnvironmentsData';
import { ActivityLogsIntegrationScopeType } from 'shared/types';

const OPTIONS: {
  value: ActivityLogsIntegrationScopeType;
  label: string;
  description: string;
}[] = [
  {
    value: 'ALL',
    label: 'All logs',
    description: 'Account-level events plus every environment.',
  },
  {
    value: 'ACCOUNT_ONLY',
    label: 'Account-level only',
    description:
      'Only events that are not related to a specific environment (e.g. user logins, role changes).',
  },
  {
    value: 'ENV_SPECIFIC',
    label: 'Specific environment',
    description: 'Only events from the selected environment.',
  },
];

const LogsIntegrationScopeField = () => {
  const { control, setValue } = useFormContext();
  const scope = useWatch({ control, name: 'scope' }) as
    | ActivityLogsIntegrationScopeType
    | undefined;
  const envId = useWatch({ control, name: 'env_id' }) as string | undefined;

  const environmentsQuery = useEnvironmentsData();

  const environments = useMemo(
    () => environmentsQuery.data ?? [],
    [environmentsQuery.data],
  );

  // Clear env_id whenever the user picks a non-ENV_SPECIFIC scope, so the API
  // payload doesn't fail the scope/env_id invariant.
  useEffect(() => {
    if (scope !== 'ENV_SPECIFIC' && envId) {
      setValue('env_id', null, { shouldValidate: true, shouldDirty: true });
    }
  }, [scope, envId, setValue]);

  const selectedDescription = useMemo(
    () => OPTIONS.find((o) => o.value === scope)?.description,
    [scope],
  );

  return (
    <FlexColumn sx={{ gap: 1 }}>
      <Typography variant="subtitle2">Log scope</Typography>
      <Controller
        control={control}
        name="scope"
        defaultValue="ALL"
        render={({ field }) => (
          <ToggleButtonGroup
            value={field.value ?? 'ALL'}
            exclusive
            fullWidth
            onChange={(_, val) => {
              if (val) field.onChange(val);
            }}
          >
            {OPTIONS.map((opt) => (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                sx={{ textTransform: 'none' }}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      />
      {selectedDescription && (
        <FormHelperText>{selectedDescription}</FormHelperText>
      )}
      {scope === 'ENV_SPECIFIC' && (
        <Controller
          control={control}
          name="env_id"
          rules={{
            validate: (value: string | null | undefined) =>
              !!value || 'Select an environment',
          }}
          render={({ field, fieldState }) => (
            <FormControl
              fullWidth
              size="small"
              error={!!fieldState.error}
              disabled={environmentsQuery.isLoading}
            >
              <InputLabel id="logs-integration-env-id-label">
                Environment
              </InputLabel>
              <Select
                labelId="logs-integration-env-id-label"
                label="Environment"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value || null)}
              >
                {environments.map((env) => (
                  <MenuItem key={env.id} value={env.id}>
                    {env.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldState.error?.message && (
                <FormHelperText>{fieldState.error.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      )}
    </FlexColumn>
  );
};

export default LogsIntegrationScopeField;
